import { useCallback, useEffect, useMemo, useState } from 'react'

import { roundLabel, useApprovals } from './approvals'
import type { Round } from './approvals'
import { DatePicker } from './DatePicker'
import { monthRangeThai, todayThai } from './datetime'

/* ============================================================================
   ตัวเลือกช่วงเวลา 3 โหมด — ใช้ร่วมกันในหน้ารายงาน

     รอบเงินเดือน : วันที่ 21 ถึง 20 ของเดือนถัดไป (ค่าเริ่มต้น)
     เดือน        : วันที่ 1 ถึงสิ้นเดือน
     กำหนดเอง     : เลือกวันเริ่ม-วันจบเอง

   แบบเดียวกับ PeriodFilter ของ frontend2026 (simatlabel) ที่มี "รอบ OT" กับ
   "ปี เดือน" — เพิ่มโหมดกำหนดเองไว้ด้วย เพราะของเดิมในหน้านี้เป็นช่องวันที่
   อยู่แล้ว ถ้าตัดทิ้งคนที่เคยเลือกช่วงแปลก ๆ จะทำไม่ได้

   ── ⚠️ รอบมาจาก HR_OT_PERIOD ไม่ใช่คำนวณเอง ────────────────────────────────
   ใช้ /api/admin/approvals/rounds ซึ่งอ่านตารางเดียวกับที่หน้าอนุมัติใช้
   ห้ามคำนวณ "วันที่ 21" เองฝั่งหน้าจอ — ถ้าวันหลัง HR เปลี่ยนวันตัดรอบ
   ตารางจะเปลี่ยนที่เดียวแล้วทุกหน้าตามทันที ส่วนที่คำนวณเองจะเพี้ยนเงียบ ๆ
   ============================================================================ */

export type PeriodMode = 'round' | 'month' | 'custom'

export type PeriodValue = {
  from: string
  to: string
  mode: PeriodMode
  /** เฉพาะ mode='round' — เอาไว้โชว์ว่าเป็นรอบไหน */
  roundId?: string
}

const MONTH_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

function monthBounds(year: number, month0: number) {
  const mm = String(month0 + 1).padStart(2, '0')
  const last = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate()
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` }
}

export function PeriodFilter({
  value,
  onChange,
  disabled,
}: {
  value: PeriodValue | null
  onChange: (v: PeriodValue) => void
  disabled?: boolean
}) {
  const api = useApprovals()

  const [mode, setMode] = useState<PeriodMode>(value?.mode ?? 'round')
  const [rounds, setRounds] = useState<Round[]>([])
  const [roundId, setRoundId] = useState(value?.roundId ?? '')

  const today = todayThai()
  const [year, setYear] = useState(() => Number(today.slice(0, 4)))
  const [month0, setMonth0] = useState(() => Number(today.slice(5, 7)) - 1)

  const [from, setFrom] = useState(value?.from ?? monthRangeThai().from)
  const [to, setTo] = useState(value?.to ?? monthRangeThai().to)

  const years = useMemo(() => {
    const now = Number(today.slice(0, 4))
    return Array.from({ length: 6 }, (_, i) => now - i)
  }, [today])

  /* โหลดรอบครั้งเดียว แล้วเลือกรอบที่ครอบวันนี้เป็นค่าเริ่มต้น
     ถ้าโหลดไม่ได้ ไม่ต้องทำให้ทั้งหน้าพัง — สลับไปโหมดเดือนได้อยู่ */
  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const r = await api.rounds()
        if (!alive) return
        setRounds(r.rounds)
        setRoundId((prev) => prev || r.current || r.rounds[0]?.roundId || '')
      } catch {
        if (alive) setRounds([])
      }
    })()
    return () => {
      alive = false
    }
  }, [api])

  const current = useMemo(
    () => rounds.find((r) => r.roundId === roundId) ?? null,
    [rounds, roundId],
  )

  /* คิดช่วงจากโหมดที่เลือก แล้วแจ้งออกไป — ส่งเฉพาะตอนค่าเปลี่ยนจริง
     ไม่งั้น onChange จะยิงทุก render แล้วหน้าแม่โหลดข้อมูลวนไม่จบ */
  const emit = useCallback(
    (v: PeriodValue) => {
      if (value && value.from === v.from && value.to === v.to && value.mode === v.mode) return
      onChange(v)
    },
    [onChange, value],
  )

  useEffect(() => {
    if (mode === 'round') {
      if (current) emit({ from: current.from, to: current.to, mode, roundId: current.roundId })
      return
    }
    if (mode === 'month') {
      const b = monthBounds(year, month0)
      emit({ ...b, mode })
      return
    }
    if (from && to) emit({ from, to, mode })
  }, [mode, current, year, month0, from, to, emit])

  return (
    <>
      {/* ปุ่มสลับโหมด — ใช้คลาสชิปเดิมของระบบ ไม่สร้างสไตล์ใหม่ */}
      <div className="chips">
        {(
          [
            ['round', 'รอบเงินเดือน'],
            ['month', 'รายเดือน'],
            ['custom', 'กำหนดเอง'],
          ] as [PeriodMode, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            className={mode === key ? 'chipbtn chipbtn--on' : 'chipbtn'}
            onClick={() => setMode(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'round' && (
        <label>
          รอบ
          <select
            value={roundId}
            disabled={disabled || rounds.length === 0}
            onChange={(e) => setRoundId(e.target.value)}
          >
            {rounds.length === 0 && <option value="">— โหลดรอบไม่ได้ —</option>}
            {rounds.map((r) => (
              <option key={r.roundId} value={r.roundId}>
                {roundLabel(r)}
              </option>
            ))}
          </select>
        </label>
      )}

      {mode === 'month' && (
        <>
          <label>
            ปี
            <select value={year} disabled={disabled} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label>
            เดือน
            <select
              value={month0}
              disabled={disabled}
              onChange={(e) => setMonth0(Number(e.target.value))}
            >
              {MONTH_TH.map((m, i) => (
                <option key={m} value={i}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      {mode === 'custom' && (
        <>
          <label>
            ตั้งแต่วันที่
            <DatePicker value={from} onChange={setFrom} />
          </label>
          <label>
            ถึงวันที่
            <DatePicker value={to} onChange={setTo} min={from} />
          </label>
        </>
      )}
    </>
  )
}
